import { requireValue } from '../../../shared/require_value.js';
import { useState } from 'react';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import type { FlightCheaperDatesBlock, ChatUiLegHeat } from '@voll-intelligence/genui-contract';
import { CardFooter } from '../../../shared/ui/card.js';
import { fmtCalendarDayLabel, weekdayInitials } from '../../../shared/ui/calendar_labels.js';
import { fmtDate, fmtDayMonth, localDayIso } from '../../../shared/ui/flight_format.js';
import { BLOCK_ELEVATION } from '../../../shared/ui/block_elevation.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';
import './flight_cheaper_dates.css';

function LegHeatGrid({
  leg,
  label,
  selected,
  onPick,
  readOnly,
  locale,
}: {
  readonly leg: ChatUiLegHeat;
  readonly label: string;
  readonly selected?: string;
  readonly onPick: (date: string) => void;
  readonly readOnly: boolean;
  readonly locale: ReturnType<typeof useGenUiI18n>['locale'];
}) {
  // Ordena antes de usar: o schema garante não-vazio mas não a ordem.
  const days = [...leg.days].sort((a, b) => a.date.localeCompare(b.date));
  // Offset do 1º dia na grade semanal (display). UTC: as datas são naive YYYY-MM-DD.
  const offset = new Date(`${requireValue(days[0]).date}T00:00:00Z`).getUTCDay();
  // "Hoje" no fuso de quem olha — datas anteriores são passado e não podem ser
  // escolhidas (defesa de UI; o provider também não deve emitir dias passados).
  // Era UTC, e à noite no Brasil isso empurrava o piso um dia à frente: a data de
  // hoje virava passado e saía de alcance. Ver `localDayIso`.
  const todayIso = localDayIso();
  // Cabeçalho da semana no idioma da sessão — a MESMA tabela do calendário do
  // kit (`weekdayInitials`), não uma cópia pt-BR local.
  const wd = weekdayInitials(locale);
  return (
    <div className="cd-leg">
      <div className="cd-leg-label">{label}</div>
      <div className="cd-grid">
        {wd.map((w, i) => (
          <div key={`h${String(i)}`} className="cd-wd">
            {w}
          </div>
        ))}
        {Array.from({ length: offset }, (_, i) => (
          <div key={`e${String(i)}`} className="cd-empty" />
        ))}
        {days.map((day) => {
          const isPast = day.date < todayIso;
          const cls = [
            'cd-cell',
            `cd-t${String(day.tier)}`,
            day.date === leg.anchorDate ? 'is-anchor' : '',
            day.date === selected ? 'is-sel' : '',
            isPast ? 'is-past' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={day.date}
              type="button"
              className={cls}
              disabled={readOnly || isPast}
              aria-label={fmtCalendarDayLabel(day.date, locale)}
              onClick={() => {
                onPick(day.date);
              }}
            >
              <span className="cd-d">{Number(day.date.slice(8, 10))}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FlightCheaperDatesCard({
  ui,
  note,
  readOnly = false,
  dispatch,
}: {
  readonly ui: FlightCheaperDatesBlock;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly dispatch: GenUiDispatch;
}) {
  const { t, locale } = useGenUiI18n();
  const [out, setOut] = useState<string | undefined>(undefined);
  const [ret, setRet] = useState<string | undefined>(undefined);
  const roundTrip = Boolean(ui.return);
  const ready = Boolean(out) && (!roundTrip || Boolean(ret));
  const { origin, destination, originLabel, destinationLabel } = ui.route;
  const o = originLabel ?? origin;
  const d = destinationLabel ?? destination;

  // Texto cosmético + data estruturada. O agents lê `data` (não o texto) para
  // rotear direto ao search_flights, evitando o loop gate pré-ofertas.
  //
  // O texto passa pelo dicionário (#2327): `t` já estava em escopo aqui e a frase
  // era montada à mão em pt-BR do mesmo jeito. Variante FECHADA por forma de
  // viagem, e a data formatada como o card a MOSTRA (`15 set`) em vez do ISO cru —
  // o eco é a fala que a pessoa aparenta ter dito, e ninguém diz "2026-09-15".
  const search = (outDate: string, retDate?: string) => {
    const day = (iso: string) => fmtDayMonth(iso, locale) || iso;
    const base = {
      origin: o,
      destination: d,
      originCode: origin,
      destinationCode: destination,
      outbound: day(outDate),
    };
    dispatch({
      type: 'interaction',
      interactionId: ui.challengeId,
      text: retDate
        ? t('flightCheaperDates.searchTextRoundTrip', { ...base, return: day(retDate) })
        : t('flightCheaperDates.searchTextOneWay', base),
      data: { outboundDate: outDate, ...(retDate ? { returnDate: retDate } : {}) },
    });
  };

  return (
    <div className={`cd ${BLOCK_ELEVATION}`}>
      <div className="cd-body">
        <div className="cd-head">
          <div className="cd-head-titles">
            <div className="cd-route">
              {origin} {roundTrip ? '⇄' : '→'} {destination}
            </div>
            <div className="cd-triptype">
              {roundTrip ? t('flightCheaperDates.roundTrip') : t('flightCheaperDates.oneWay')}
            </div>
          </div>
        </div>
        <LegHeatGrid
          leg={ui.outbound}
          label={roundTrip ? t('flight.quote.outbound') : t('flightCheaperDates.dates')}
          selected={out}
          onPick={setOut}
          readOnly={readOnly}
          locale={locale}
        />
        {ui.return && (
          <LegHeatGrid
            leg={ui.return}
            label={t('flight.quote.return')}
            selected={ret}
            onPick={setRet}
            readOnly={readOnly}
            locale={locale}
          />
        )}
        <div className="cd-legend">
          <span>{t('flightCheaperDates.cheapest')}</span>
          <span className="cd-bar" />
          <span>{t('flightCheaperDates.mostExpensive')}</span>
        </div>
      </div>
      <CardFooter note={note}>
        {!readOnly && (
          <div className="fui-actions">
            <button
              type="button"
              className="fui-btn fui-btn-primary fui-btn-lg"
              disabled={!ready}
              onClick={() => {
                search(requireValue(out), ret);
              }}
            >
              {t('flightCheaperDates.search')}
            </button>
            {ui.outbound.anchorDate && (!roundTrip || ui.return?.anchorDate) && (
              <button
                type="button"
                className="fui-btn fui-btn-outline fui-btn-lg"
                onClick={() => {
                  search(requireValue(ui.outbound.anchorDate), ui.return?.anchorDate);
                }}
              >
                {t('flightCheaperDates.keep', { date: fmtDate(ui.outbound.anchorDate, locale) })}
              </button>
            )}
          </div>
        )}
      </CardFooter>
    </div>
  );
}
